import axios from 'axios';
import { API_BASE_URL } from '../config';
import { attachAuthInterceptors } from './attachAuthInterceptors';
import { refreshSession, reauthenticateTelegram } from './authService';

console.log('🌐 Используется API:', API_BASE_URL);

const api = axios.create({
  baseURL: API_BASE_URL,
});
attachAuthInterceptors(api, { refreshSession, reauthenticateTelegram });

const authHeaders = (token) => ({
  headers: {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  },
});

const authHeadersForm = (token) => ({
  headers: {
    Authorization: `Bearer ${token}`,
  },
});

export const fetchFilteredEvents = async (token, activeServer, dateRange) => {
  if (!activeServer || !dateRange.from || !dateRange.to) return;

  const eventTypes = [
    'orderDeleted',
    'orderCancelPrecheque',
    'orderReturned',
    'orderPaidNoCash',
    'orderPrepayReturned',
    'dishesMovedAfterCheque',
    'dishesMoved',
    'dishesMovedTo',
    'deletedNewItems',
    'deletedPrintedItems',
    'openPriceApplied',
    'serviceChequeRePrinted',
  ];

  const fromTime = `${dateRange.from}T00:00:00.000`;
  const toTime = `${dateRange.to}T23:59:59.999`;

  try {
    const response = await api.post(
      `/data/filtered_events?from_time=${fromTime}&to_time=${toTime}`,
      eventTypes,
      authHeaders(token)
    );
    return response.data;
  } catch (error) {
    console.error('Ошибка при получении событий:', error.message);
    throw error;
  }
};

export const fetchRevenueData = async (token, activeServer, dateRange) => {
  if (!activeServer || !dateRange.from || !dateRange.to) return;
  try {
    const response = await api.get(
      `/data/revenue?from_date=${dateRange.from}&to_date=${dateRange.to}`,
      authHeaders(token)
    );
    return response.data?.revenue ?? [];
  } catch (error) {
    console.error('Ошибка при получении данных о выручке:', error.message);
    throw error;
  }
};

// untilNow — только заказы, закрытые до текущего часа ресторана (данные сравнения для «Сегодня»)
export const fetchSalesByPaymentType = async (token, activeServer, dateRange, { untilNow = false } = {}) => {
  if (!activeServer || !dateRange.from || !dateRange.to) return null;

  try {
    const response = await api.get(
      `/data/sales_by_payment?from_date=${dateRange.from}&to_date=${dateRange.to}${untilNow ? '&until_now=true' : ''}`,
      authHeaders(token)
    );

    // 🟢 Возвращаем целиком объект sales_data, как есть
    const salesData = response.data?.sales_data || null;

    if (salesData) {
      console.log(
        "📊 sales_by_payment получен:",
        Array.isArray(salesData.data)
          ? `data[${salesData.data.length}]`
          : Object.keys(salesData)
      );
    }

    return salesData;
  } catch (error) {
    console.error("Ошибка при получении данных о продажах по типам оплат:", error.message);
    throw error;
  }
};



// 🔹 Текущий учётный день активного сервера ('yyyy-MM-dd'); бэкенд при ошибках iiko отдаёт дату по MSK
export const fetchBusinessDay = async (token) => {
  const response = await api.get('/data/business_day', authHeadersForm(token));
  return response.data?.business_day || null;
};

// 🔹 Получение данных анализа себестоимости
export const fetchCostAnalysisData = async (token, activeServer, dateRange) => {
  if (!activeServer || !dateRange.from || !dateRange.to) return null;

  try {
    const response = await api.get(
      `/data/cost_analysis?from_date=${dateRange.from}&to_date=${dateRange.to}`,
      authHeaders(token)
    );

    const costData = response.data?.cost_data || null;

    if (costData) {
      console.log(
        "📊 cost_analysis получен:",
        Array.isArray(costData.data)
          ? `data[${costData.data.length}]`
          : Object.keys(costData)
      );
    }

    return costData;
  } catch (error) {
    console.error("Ошибка при получении данных анализа себестоимости:", error.message);
    throw error;
  }
};

// ABC-анализ меню: сырой OLAP по ресторанам, группам, блюдам и категориям (буквы и итоги — abcCalculations)
export const fetchMenuAbcData = async (token, activeServer, dateRange) => {
  if (!activeServer || !dateRange.from || !dateRange.to) return null;
  const response = await api.get(
    `/data/menu_abc?from_date=${dateRange.from}&to_date=${dateRange.to}`,
    authHeaders(token)
  );
  return response.data?.abc_data || null;
};

export default api;

export const fetchPlacementAds = async (token, placement) => {
  const response = await api.get(
    `/data/promo/placements/${placement}`,
    authHeaders(token)
  );
  return response.data || [];
};

export const listAds = async (token) => {
  const response = await api.get(`/data/promo`, authHeaders(token));
  return response.data || [];
};

export const createAd = async (token, formData) => {
  const response = await api.post(`/data/promo`, formData, authHeadersForm(token));
  return response.data;
};

export const updateAd = async (token, adId, payload) => {
  const response = await api.patch(
    `/data/promo/${adId}`,
    payload,
    authHeaders(token)
  );
  return response.data;
};

export const updateAdMedia = async (token, adId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await api.put(
    `/data/promo/${adId}/media`,
    formData,
    authHeadersForm(token)
  );
  return response.data;
};

export const deleteAd = async (token, adId) => {
  await api.delete(`/data/promo/${adId}`, authHeaders(token));
};

// === iikoCloud: админка привязок (только администратор) ===
// Ключ API отправляется только в теле запроса; в ответах — лишь последние 4 символа.
// Админка: активность пользователей по серверам — { today, keep_days, servers: [...] }
export const fetchUserActivity = async (token) => {
  const response = await api.get('/data/admin/activity', authHeaders(token));
  return response.data;
};

// Отзыв админам: { type, text, contact?, context? } → { id, reply_via: 'email' | 'telegram' | 'none' }
export const sendFeedback = async (token, body) => {
  const response = await api.post('/data/feedback', body, authHeaders(token));
  return response.data;
};

const IIKOCLOUD_ADMIN = '/data/iikocloud/admin';

export const listIikoCloudServers = async (token) => {
  const response = await api.get(`${IIKOCLOUD_ADMIN}/servers`, authHeaders(token));
  return response.data || [];
};

export const listIikoCloudConnections = async (token) => {
  const response = await api.get(`${IIKOCLOUD_ADMIN}/connections`, authHeaders(token));
  return response.data || [];
};

export const checkIikoCloudKey = async (token, apiKey) => {
  const response = await api.post(`${IIKOCLOUD_ADMIN}/check`, { api_key: apiKey }, authHeaders(token));
  return response.data;
};

// organizationIds — выбранные организации; null — все организации ключа
export const fetchIikoCloudTerminalGroupsByKey = async (token, apiKey, organizationIds) => {
  const response = await api.post(
    `${IIKOCLOUD_ADMIN}/terminal_groups`,
    { api_key: apiKey, organization_ids: organizationIds },
    authHeaders(token)
  );
  return response.data || [];
};

export const fetchIikoCloudTerminalGroups = async (token, connectionId) => {
  const response = await api.get(`${IIKOCLOUD_ADMIN}/connections/${connectionId}/terminal_groups`, authHeaders(token));
  return response.data || [];
};

export const createIikoCloudConnection = async (token, payload) => {
  const response = await api.post(`${IIKOCLOUD_ADMIN}/connections`, payload, authHeaders(token));
  return response.data;
};

export const updateIikoCloudConnection = async (token, connectionId, payload) => {
  const response = await api.patch(`${IIKOCLOUD_ADMIN}/connections/${connectionId}`, payload, authHeaders(token));
  return response.data;
};

export const replaceIikoCloudKey = async (token, connectionId, apiKey) => {
  const response = await api.put(
    `${IIKOCLOUD_ADMIN}/connections/${connectionId}/key`,
    { api_key: apiKey },
    authHeaders(token)
  );
  return response.data;
};

export const recheckIikoCloudConnection = async (token, connectionId) => {
  const response = await api.post(`${IIKOCLOUD_ADMIN}/connections/${connectionId}/recheck`, null, authHeaders(token));
  return response.data;
};

export const refreshIikoCloudDictionaries = async (token, connectionId) => {
  const response = await api.post(`${IIKOCLOUD_ADMIN}/connections/${connectionId}/refresh`, null, authHeaders(token));
  return response.data;
};

export const deleteIikoCloudConnection = async (token, connectionId) => {
  await api.delete(`${IIKOCLOUD_ADMIN}/connections/${connectionId}`, authHeaders(token));
};

export const fetchIikoCloudPlaces = async (token, connectionId) => {
  const response = await api.get(`${IIKOCLOUD_ADMIN}/connections/${connectionId}/places`, authHeaders(token));
  return response.data;
};

export const saveIikoCloudPlaces = async (token, connectionId, kitchenPlaces, knownPlaces) => {
  const response = await api.put(
    `${IIKOCLOUD_ADMIN}/connections/${connectionId}/places`,
    { kitchen_places: kitchenPlaces, known_places: knownPlaces },
    authHeaders(token)
  );
  return response.data;
};

// === iikoCloud: признак для пунктов меню и «Загрузка кухни» ===

export const fetchIikoCloudStatus = async (token) => {
  const response = await api.get('/data/iikocloud/status', authHeaders(token));
  return Boolean(response.data?.iikocloud);
};

// filter — { restaurant, group } из выбора в шапке (utils/restaurant.kitchenFilter)
export const fetchKitchenLoad = async (token, dateRange, filter = {}) => {
  const params = { from_date: dateRange.from, to_date: dateRange.to, ...filter };
  const response = await api.get('/data/kitchen/load', { ...authHeaders(token), params });
  return response.data;
};

export const fetchIikoCloudGroupMap = async (token, connectionId) => {
  const response = await api.get(`${IIKOCLOUD_ADMIN}/connections/${connectionId}/group_map`, authHeaders(token));
  return response.data;
};

export const saveIikoCloudGroupMap = async (token, connectionId, map) => {
  const response = await api.put(`${IIKOCLOUD_ADMIN}/connections/${connectionId}/group_map`, { map }, authHeaders(token));
  return response.data;
};

// Организация iikoCloud → ресторан iiko RMS (как в выборе ресторана): «Кухня» сети
export const fetchIikoCloudRestaurantMap = async (token, connectionId) => {
  const response = await api.get(`${IIKOCLOUD_ADMIN}/connections/${connectionId}/restaurant_map`, authHeaders(token));
  return response.data;
};

export const saveIikoCloudRestaurantMap = async (token, connectionId, map) => {
  const response = await api.put(`${IIKOCLOUD_ADMIN}/connections/${connectionId}/restaurant_map`, { map }, authHeaders(token));
  return response.data;
};

// Рестораны с данными iikoCloud (организации подключения): остальные на «Кухне» выбрать нельзя
export const fetchKitchenRestaurants = async (token) => {
  const response = await api.get('/data/kitchen/restaurants', authHeaders(token));
  return response.data?.restaurants || [];
};

// Стоп-лист «Кухни» (iikoCloud, только чтение); filter — { restaurant, group }
export const fetchKitchenStopList = async (token, filter = {}) => {
  const response = await api.get('/data/kitchen/stoplist', { ...authHeaders(token), params: { ...filter } });
  return response.data;
};

export const fetchKitchenLive = async (token, filter = {}) => {
  const params = { ...filter };
  const response = await api.get('/data/kitchen/live', { ...authHeaders(token), params });
  return response.data;
};

// «Склад»: позиции с отрицательным остатком (iikoCloud, только чтение); restaurant — ресторан сети или пусто (все)
export const fetchStockNegative = async (token, restaurant) => {
  const params = restaurant ? { restaurant } : {};
  const response = await api.get('/data/stock/negative', { ...authHeaders(token), params });
  return response.data;
};

// Остаток товара на складе по дням: { organization, store, product, days }
export const fetchStockHistory = async (token, params) => {
  const response = await api.get('/data/stock/product-history', { ...authHeaders(token), params });
  return response.data;
};

// В каких блюдах товар по техкартам и сколько ушло за период: { organization, store, product, days }
export const fetchStockUsage = async (token, params) => {
  const response = await api.get('/data/stock/product-usage', { ...authHeaders(token), params });
  return response.data;
};

// Сводка минусов для «Обзора» — из кеша, без запросов в iiko; restaurant — ресторан сети или пусто (все)
export const fetchStockSummary = async (token, restaurant) => {
  const params = restaurant ? { restaurant } : {};
  const response = await api.get('/data/stock/summary', { ...authHeaders(token), params });
  return response.data;
};
